// ./src/components/features/PartnerPackages/PartnerPackagesFilterBar/PartnerPackagesFilterBar.tsx
//
// Filter controls for the Partner Packages list: by status (active / disabled),
// partner kind (multi), and money direction. Fully controlled — the page owns the
// value and maps it to a PartnerPackageFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  PARTNER_KIND_VALUES,
  PARTNER_MONEY_DIRECTION_VALUES,
  PARTNER_KIND_LABELS,
  PARTNER_MONEY_DIRECTION_LABELS,
} from "@/utils/api";
import { AppIcon, MultiSelectField } from "@/components/generic";

export type PartnerPackageStatusFilter = "active" | "disabled";
export type PartnerPackageMoneyFilter = "revenue" | "expense" | "none";

export interface PartnerPackagesFilterValue {
  status: PartnerPackageStatusFilter | null;
  partnerKinds: string[];
  moneyDirection: PartnerPackageMoneyFilter | null;
}

export interface PartnerPackagesFilterBarProps {
  value: PartnerPackagesFilterValue;
  onChange: (value: PartnerPackagesFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_PARTNER_PACKAGES_FILTER: PartnerPackagesFilterValue = {
  status: null,
  partnerKinds: [],
  moneyDirection: null,
};

const isFilterActive = (value: PartnerPackagesFilterValue): boolean =>
  Boolean(value.status) || value.partnerKinds.length > 0 || Boolean(value.moneyDirection);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };
const KIND_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const MONEY_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };

const ANY = "any";

const PartnerPackagesFilterBarComponent: React.FC<PartnerPackagesFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["partnerPackages", "common"]);

  const statusOptions = [
    { value: ANY, label: t("partnerPackages:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("partnerPackages:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("partnerPackages:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const moneyOptions = [
    { value: ANY, label: t("partnerPackages:filters.money.any", { defaultValue: "Any" }) },
    ...PARTNER_MONEY_DIRECTION_VALUES.map((v) => ({
      value: v,
      label: t(`partnerPackages:money.${v}`, { defaultValue: PARTNER_MONEY_DIRECTION_LABELS[v] }),
    })),
  ];

  const kindOptions = PARTNER_KIND_VALUES.map((v) => ({
    value: v,
    label: t(`partnerPackages:kind.${v}`, { defaultValue: PARTNER_KIND_LABELS[v] }),
  }));

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("partnerPackages:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as PartnerPackageStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={KIND_STYLE}>
        <MultiSelectField
          label={t("partnerPackages:filters.kind.label", { defaultValue: "Partner type" })}
          placeholder={
            value.partnerKinds.length > 0
              ? undefined
              : t("partnerPackages:filters.kind.placeholder", { defaultValue: "Any type" })
          }
          data={kindOptions}
          value={value.partnerKinds}
          onChange={(_name, next) => onChange({ ...value, partnerKinds: next })}
          disabled={disabled}
          clearable
          searchable
          size="md"
        />
      </Box>

      <Box style={MONEY_STYLE}>
        <Input.Wrapper label={t("partnerPackages:filters.money.label", { defaultValue: "Money" })}>
          <SegmentedControl
            data={moneyOptions}
            value={value.moneyDirection ?? ANY}
            onChange={(next) =>
              onChange({ ...value, moneyDirection: next === ANY ? null : (next as PartnerPackageMoneyFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_PARTNER_PACKAGES_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const PartnerPackagesFilterBar = React.memo(PartnerPackagesFilterBarComponent);
